import * as Sentry from '@sentry/react';
import {ChakraProvider, ColorModeScript} from '@chakra-ui/react';
import {QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import {AuthProvider} from './context/AuthContext';
import {WebSocketProvider} from './context/WebSocketContext';
import {validateConfig} from './config/api';
import {queryClient} from './config/queryClient';
import Theme from './styles/Theme';
import {ErrorBoundary} from './shared/components';

// Sentry — error tracking + performance monitoring + session replay
if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
        sendDefaultPii: true,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
        ],
        // Tracing — 10% en prod, 100% en dev
        tracesSampleRate: process.env.NODE_ENV === 'development' ? 1 : 0.1,
        tracePropagationTargets: ['localhost', /^https:\/\/tools-box-seo\.com\/api/],
        // Session Replay — 10% des sessions, 100% sur erreur
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1,
    });

    // F-25 — Forward CSP violations to Sentry as warnings. The browser fires
    // SecurityPolicyViolationEvent on every violation (regardless of enforced
    // vs report-only). Without this, observed only via DevTools console in
    // live sessions — no prod telemetry. Tag `type=csp` to filter in Sentry.
    document.addEventListener("securitypolicyviolation", (event) => {
        Sentry.captureMessage(
            `CSP: ${event.violatedDirective} blocked ${event.blockedURI || "<inline>"}`,
            {
                level: "warning",
                tags: { type: "csp", directive: event.violatedDirective },
                extra: {
                    blockedURI: event.blockedURI,
                    documentURI: event.documentURI,
                    sourceFile: event.sourceFile,
                    lineNumber: event.lineNumber,
                    columnNumber: event.columnNumber,
                    sample: event.sample,
                    disposition: event.disposition,
                },
            },
        );
    });
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found in document');
const root = ReactDOM.createRoot(rootEl);

// Fail fast when required env vars are missing. Without this, an empty
// GOOGLE_CLIENT_ID makes the Google sign-in SDK throw on LoginPage mount
// and React 18 silently unmounts the entire tree — a blank page with no
// signal of what's wrong. Show an explicit screen instead.
const { valid, missing } = validateConfig();
if (valid) {
    root.render(
        <React.StrictMode>
            <ChakraProvider theme={Theme}>
                <ColorModeScript initialColorMode={Theme.config.initialColorMode}/>
                <QueryClientProvider client={queryClient}>
                    <ErrorBoundary>
                        <AuthProvider>
                            <WebSocketProvider>
                                <App/>
                            </WebSocketProvider>
                        </AuthProvider>
                    </ErrorBoundary>
                    {process.env.NODE_ENV === 'development' && (
                        <ReactQueryDevtools initialIsOpen={false}/>
                    )}
                </QueryClientProvider>
            </ChakraProvider>
        </React.StrictMode>
    );
} else {
    // Plain inline styles on purpose: avoid pulling Chakra/theme here in
    // case the missing config also breaks them. Anyone who sees this is
    // a developer/operator, not an end user.
    root.render(
        <div style={{padding: '2rem', fontFamily: 'system-ui, sans-serif', maxWidth: '640px', margin: '2rem auto', color: '#1a202c'}}>
            <h1 style={{color: '#c53030', marginTop: 0}}>Configuration manquante</h1>
            <p>Les variables d&apos;environnement suivantes sont requises et absentes&nbsp;:</p>
            <ul>
                {missing.map((key) => (
                    <li key={key}><code>REACT_APP_{key}</code></li>
                ))}
            </ul>
            <p>Vérifier le fichier <code>.env</code> à la racine du projet, ou la configuration du build.</p>
        </div>
    );
}

